import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout, { adminFetch, SectionTitle, AdminCard, Badge, useAdmin } from './AdminLayout';

const JOBS = [
  { id: 'update-db',         label: 'Mettre à jour la base aides',   icon: '📋', color: '#D4724A' },
  { id: 'update-seo',        label: 'Mettre à jour le SEO',          icon: '🔍', color: '#5C8C6E' },
  { id: 'generate-articles', label: 'Générer articles blog',         icon: '✍️', color: '#F0A500' },
];

const CRON_SCHEDULE = [
  { job: 'Mise à jour base aides',  cron: 'Tous les lundis à 03:00',    icon: '📋' },
  { job: 'Vérification conformité', cron: 'Tous les 15 du mois à 04:00', icon: '✅' },
  { job: 'Génération sitemap',      cron: 'Tous les jours à 02:00',     icon: '🗺️' },
];

export default function AdminLogs() {
  const [data, setData]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [running, setRunning]   = useState(null);
  const [messages, setMessages] = useState({});

  // Phase 6 / G2 — endpoint TS sous /api/admin/monitoring/logs (shape
  // identique : { cron, errors, has_errors }).
  const load = useCallback(() => {
    adminFetch('/api/admin/monitoring/logs')
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setData(d); setLoadError(false); })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [load]);

  const runJob = async (jobId) => {
    setRunning(jobId);
    setMessages(m => ({ ...m, [jobId]: null }));
    try {
      const res = await adminFetch(`/api/admin/run-job/${jobId}`, { method: 'POST' });
      const d = await res.json();
      if (d.success) {
        setMessages(m => ({ ...m, [jobId]: { type: 'success', message: `Lancé le ${new Date(d.timestamp).toLocaleString('fr-FR')}` } }));
      } else {
        setMessages(m => ({ ...m, [jobId]: { type: 'error', message: d.error || 'Impossible de lancer ce job. Réessayez dans un instant.' } }));
      }
      setTimeout(load, 500);
    } catch {
      setMessages(m => ({ ...m, [jobId]: { type: 'error', message: 'Erreur de communication avec le serveur.' } }));
    }
    setRunning(null);
  };

  return (
    <AdminLayout title="Logs & Cron Jobs">
      <Content data={data} loading={loading} loadError={loadError} running={running} messages={messages} onRunJob={runJob} onRefresh={load} />
    </AdminLayout>
  );
}

function Content({ data, loading, loadError, running, messages, onRunJob, onRefresh }) {
  const { dark } = useAdmin();
  const textColor  = dark ? '#e0e0e0' : '#2D2D2D';
  const mutedColor = '#8A8A8A';

  if (loading) return <p style={{ color: mutedColor, padding: 16 }}>Chargement…</p>;
  if (loadError || !data) {
    return (
      <div style={{
        padding: 16, borderRadius: 10, background: '#FDECEA',
        border: '1px solid rgba(192,57,43,0.3)', color: '#933',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <span>Erreur de chargement des logs. Vérifiez votre connexion.</span>
        <button onClick={onRefresh} style={{
          padding: '8px 16px', borderRadius: 8, border: 'none',
          background: '#D4724A', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>Réessayer</button>
      </div>
    );
  }

  const { cron, errors, has_errors } = data;

  return (
    <div>
      {/* Alerte erreurs */}
      {has_errors && (
        <div style={{
          background: '#FDECEA', border: '1px solid rgba(192,57,43,0.4)',
          borderRadius: 12, padding: '14px 20px', marginBottom: 24,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span style={{ fontSize: 24 }}>🚨</span>
          <div>
            <strong style={{ color: '#C0392B', fontSize: 15 }}>Des erreurs sont présentes dans errors.log</strong>
            <div style={{ fontSize: 13, color: '#5A5A5A', marginTop: 2 }}>
              Consultez la section ci-dessous et résolvez les erreurs avant le prochain déploiement.
            </div>
          </div>
        </div>
      )}

      {/* Cron jobs schedule */}
      <AdminCard style={{ marginBottom: 24 }}>
        <SectionTitle>Planification des tâches cron</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {CRON_SCHEDULE.map((c, i) => (
            <div key={i} style={{
              background: dark ? '#1a1a2e' : '#F5F3F0', borderRadius: 10, padding: '16px',
              display: 'flex', alignItems: 'flex-start', gap: 12,
            }}>
              <div style={{ fontSize: 24, flexShrink: 0 }}>{c.icon}</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: textColor, marginBottom: 4 }}>{c.job}</div>
                <div style={{ fontSize: 12, color: '#5C8C6E', fontWeight: 500 }}>{c.cron}</div>
              </div>
            </div>
          ))}
        </div>
      </AdminCard>

      {/* Actions manuelles */}
      <AdminCard style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <SectionTitle>Exécution manuelle</SectionTitle>
          <button onClick={onRefresh} style={{
            padding: '8px 14px', borderRadius: 8, border: '1px solid #D9D9D9',
            background: 'transparent', color: '#D4724A', fontSize: 13, cursor: 'pointer',
          }}>↻ Actualiser</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {JOBS.map(job => (
            <div key={job.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 20px', background: dark ? '#1a1a2e' : '#F5F3F0',
              borderRadius: 10, gap: 16, flexWrap: 'wrap',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>{job.icon}</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: textColor }}>{job.label}</div>
                  {messages[job.id] && (
                    <div style={{
                      fontSize: 12, marginTop: 2,
                      color: messages[job.id].type === 'error' ? '#933' : '#3D8B5E',
                    }}>
                      {messages[job.id].type === 'error' ? '⚠ ' : '✓ '}{messages[job.id].message}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => onRunJob(job.id)}
                disabled={!!running}
                style={{
                  padding: '10px 20px', borderRadius: 10, border: 'none',
                  background: running === job.id || !!running ? '#D9D9D9' : job.color,
                  color: !!running ? '#8A8A8A' : '#fff',
                  fontSize: 14, fontWeight: 600, cursor: running ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {running === job.id ? 'En cours…' : 'Lancer maintenant'}
              </button>
            </div>
          ))}
        </div>
      </AdminCard>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Cron log */}
        <AdminCard>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <SectionTitle>cron.log</SectionTitle>
            <Badge color="#5C8C6E">{cron.length} entrées</Badge>
          </div>
          <div style={{
            background: dark ? '#0f0f23' : '#1e1e2e', borderRadius: 8, padding: '16px',
            fontFamily: 'JetBrains Mono, monospace', fontSize: 11, lineHeight: 1.7,
            maxHeight: 400, overflowY: 'auto', color: '#a8d8a8',
          }}>
            {cron.length === 0 ? (
              <span style={{ color: '#5a5a7a' }}>Aucune entrée de log cron.</span>
            ) : (
              [...cron].reverse().map((line, i) => {
                const isError   = line.toLowerCase().includes('error') || line.toLowerCase().includes('erreur');
                const isSuccess = line.includes('✓') || line.includes('succès') || line.includes('OK');
                const isManual  = line.includes('[MANUEL]');
                return (
                  <div key={i} style={{
                    padding: '2px 0',
                    color: isError ? '#ff6b6b' : isSuccess ? '#a8d8a8' : isManual ? '#ffd166' : '#8888aa',
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                  }}>{line}</div>
                );
              })
            )}
          </div>
        </AdminCard>

        {/* Errors log */}
        <AdminCard>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <SectionTitle>errors.log</SectionTitle>
            {errors.length > 0
              ? <Badge color="#C0392B">{errors.length} erreur{errors.length > 1 ? 's' : ''}</Badge>
              : <Badge color="#3D8B5E">Aucune erreur</Badge>
            }
          </div>
          <div style={{
            background: dark ? '#1a0f0f' : '#1e1414', borderRadius: 8, padding: '16px',
            fontFamily: 'JetBrains Mono, monospace', fontSize: 11, lineHeight: 1.7,
            maxHeight: 400, overflowY: 'auto', color: '#ff9999',
          }}>
            {errors.length === 0 ? (
              <span style={{ color: '#5a8a5a' }}>✓ Aucune erreur dans le log. Tout fonctionne correctement.</span>
            ) : (
              [...errors].reverse().map((line, i) => (
                <div key={i} style={{
                  padding: '2px 0', color: '#ff6b6b',
                  borderBottom: '1px solid rgba(255,100,100,0.1)',
                }}>{line}</div>
              ))
            )}
          </div>
          {errors.length > 0 && (
            <div style={{
              marginTop: 12, padding: '10px 14px', background: '#FDECEA',
              borderRadius: 8, fontSize: 12, color: '#C0392B',
            }}>
              Des erreurs ont été détectées. Vérifiez les logs et relancez les services si nécessaire.
            </div>
          )}
        </AdminCard>
      </div>
    </div>
  );
}
